import moment from 'moment';

function asOptionList(options) {
  if (!options) {
    return [];
  }
  return Array.isArray(options) ? options : Object.values(options);
}

function momentFormatFromPhp(phpFormat) {
  if (!phpFormat) {
    return 'MM/DD/YYYY';
  }
  return phpFormat
    .replace(/Y/g, 'YYYY')
    .replace(/y/g, 'YY')
    .replace(/m/g, 'MM')
    .replace(/d/g, 'DD');
}

function toCompareDateString(value) {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    return moment.unix(Number(value)).utc().format('MM/DD/YYYY');
  }
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : null;
}

function findPeriodOptionByDates(options, start, end) {
  const startNorm = toCompareDateString(start);
  const endNorm = toCompareDateString(end);
  if (!startNorm || !endNorm) {
    return null;
  }
  return asOptionList(options).find((option) => {
    const format = momentFormatFromPhp(option.format);
    const optionStart = moment.utc(option.start, format).format('MM/DD/YYYY');
    const optionEnd = moment.utc(option.end, format).format('MM/DD/YYYY');
    return optionStart === startNorm && optionEnd === endNorm;
  }) ?? null;
}

/** Goal's selected period from filterOption (stats === 1). */
export function selectedGoalFilterOption(goalDetails) {
  return asOptionList(goalDetails?.filterOption).find((option) => Number(option.stats) === 1) ?? null;
}

/** Display fiscal year from a period row (fiscalYear + yearOffset, else calendar year). */
export function displayFiscalYearFromPeriodRow(row) {
  if (row?.fiscalYear != null) {
    return Number(row.fiscalYear) + Number(row.yearOffset || 0);
  }
  return row?.year != null ? Number(row.year) : null;
}

/** Map a backend period row to kr_duration state (use fiscalYear for labels, not calendar year). */
export function periodRowToKrDuration(row) {
  if (!row) {
    return null;
  }
  return {
    start: row.start,
    end: row.end,
    name: row.name,
    year: displayFiscalYearFromPeriodRow(row),
    value: row.value ?? null,
    qname: row.quarter_name || '',
    isQuarter: row.isQuarter || false,
    isCustom: false,
  };
}

/** Period row shaped for DurationSelect (display fiscal year in year). */
export function periodRowForDurationSelect(row) {
  if (!row) {
    return null;
  }
  if (row.fiscalYear != null) {
    return { ...row, year: displayFiscalYearFromPeriodRow(row) };
  }
  return row;
}

/**
 * KR duration from saved metric dates; null when metric uses full goal period (no custom dates).
 */
export function metricDurationFromItem(item, goalDetails) {
  if (!item) {
    return null;
  }

  let start = item.start_date;
  let end = item.end_date;
  if (item.startDateText && item.endDateText) {
    start = item.startDateText;
    end = item.endDateText;
  }

  if (!start && !end) {
    return null;
  }

  const matched =
    findPeriodOptionByDates(goalDetails?.metricQuarterOption, start, end)
    || findPeriodOptionByDates(goalDetails?.filterOption, start, end)
    || findPeriodOptionByDates(goalDetails?.annualQuarterOption, start, end);

  if (matched) {
    return periodRowToKrDuration(matched);
  }

  const startNorm = toCompareDateString(start);
  const endNorm = toCompareDateString(end);
  if (!startNorm || !endNorm) {
    return null;
  }

  return {
    start: startNorm,
    end: endNorm,
    name: '',
    year: null,
    value: null,
    qname: '',
    isQuarter: false,
    isCustom: true,
  };
}

/**
 * kr_duration state, else metric dates, else goal period (stats === 1).
 */
export function resolveKrDuration(item, goalDetails, krDurationState) {
  if (krDurationState?.start) {
    return krDurationState;
  }
  const fromMetric = metricDurationFromItem(item, goalDetails);
  if (fromMetric) {
    return fromMetric;
  }
  return periodRowToKrDuration(selectedGoalFilterOption(goalDetails));
}

/** initialValue for DurationSelect: metric → goal, with fiscal year on period rows. */
export function resolveDurationSelectInitial(item, goalDetails, krDurationState) {
  const resolved = resolveKrDuration(item, goalDetails, krDurationState);
  if (!resolved) {
    const goalRow = selectedGoalFilterOption(goalDetails);
    return periodRowForDurationSelect(goalRow);
  }
  if (resolved.isCustom) {
    return resolved;
  }
  const goalRow = selectedGoalFilterOption(goalDetails);
  const displayYear = resolved.year ?? displayFiscalYearFromPeriodRow(goalRow);
  return {
    ...resolved,
    year: displayYear ?? resolved.year,
  };
}

export function isKrTimespanWithinObjective(krDuration, goalDetails) {
  if (
    !krDuration?.start
    || !krDuration?.end
    || !goalDetails?.start_date
    || !goalDetails?.target_date
  ) {
    return true;
  }
  const goalStart = moment.utc(goalDetails.start_date).startOf('day');
  const goalEnd = moment.utc(goalDetails.target_date).startOf('day');
  const krStart = moment.utc(krDuration.start).startOf('day');
  const krEnd = moment.utc(krDuration.end).startOf('day');
  if (!krStart.isValid() || !krEnd.isValid() || !goalStart.isValid() || !goalEnd.isValid()) {
    return true;
  }
  return !krStart.isBefore(goalStart) && !krEnd.isAfter(goalEnd);
}
